import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useHistory, useParams } from 'react-router-dom'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import SortingBlock from 'components/Modules/Slots/SlotsListing/components/SortingBlock/SortingBlock'
import ClientsList from 'components/Modules/Slots/SlotsListing/components/ClientsList/ClientsList'
import Loader from 'components/UI/Loader/Loader'
import ContentPagination from 'components/ContentBox/Global/ContentPagination/ContentPagination'
import slotsListingStore, { sortingOptions } from 'Stores/slotsListingStore'
import Stores from 'Stores'
import { localisationMaster } from 'Localisation/Localisation'
import { dateFormat } from 'helpers/date'
import { superAxiosRequest } from 'axiosApi'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'

const initPagination = {
  page: 1,
  limit: 10,
}

function SlotListing() {
  const { id: pageType } = useParams()
  const { levels, totalClientsCount, sortingType, fetchClients, fetchLevels } =
    slotsListingStore
  const { isClient } = Stores
  const [isLoading, setIsLoading] = useState(true)
  const [clientsPerPage, setClientsPerPage] = useState(10)
  const [page, setPage] = useState(1)
  const [filterData, setFilterData] = useState()

  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore

  const [filter, updateFilter] = useLocalStorageFilter()
  const history = useHistory()

  const filterConfig = [
    {
      type: 'input',
      label: 'Имя клиента',
      placeholder: 'Имя клиента',
      nameState: 'name',
    },
    {
      type: 'date_day_picker',
      label: 'Период',
      data: {
        date_from: '',
        date_until: '',
      },
      nameState: ['date_from', 'date_until'],
    },
    {
      type: 'select',
      label: 'Локация',
      placeholder: 'Локация',
      values: filterData?.locations,
      sort_key: 'name',
      nameState: 'location',
    },
  ]

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      search: 'Search',
    },
  })

  useEffect(() => {
    if (!isLoading) {
      setIsLoading(true)
    }
    const { ordered_by, reverse } = sortingOptions?.find(
      (item) => item.value === sortingType,
    )

    // Преобразование фильтров для API
    let apiFilterParams = { ...filter }
    if (filter?.date_from) {
      apiFilterParams.date_from = dateFormat(filter?.date_from, 'YYYY-mm-dd')
    }
    if (filter?.date_until) {
      apiFilterParams.date_until = dateFormat(filter?.date_until, 'YYYY-mm-dd')
    }

    fetchClients(
      {
        is_level: pageType === 'past',
        offset: clientsPerPage * (page - 1),
        limit: clientsPerPage,
        ordered_by,
        reverse,
        ...apiFilterParams,
      },
      () => setIsLoading(false),
    )
  }, [clientsPerPage, page, pageType, sortingType, totalClientsCount, filter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (levels.length > 0 || isClient) {
      return
    }
    fetchLevels()
    superAxiosRequest({
      method: 'get',
      link: 'groups/student_status_changes',
    }).then(({ data }) => setFilterData(data))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  const handleClearSearch = () => {
    const { name, ...otherFilters } = filter
    updateFilter(otherFilters)
    setTextValues(filterTextValues.filter((field) => field.key !== 'name'))
    setPage(1)
  }

  const handleSearch = (searchValue) => {
    updateFilter({
      ...filter,
      name: searchValue,
    })
    changeTextValue('name', {
      key: 'name',
      label: 'Поиск по имени',
      value: searchValue,
    })
    setPage(1)
  }

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/online-schedule-v2/slots/past') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  return (
    <>
      <ContentHeader
        title={
          pageType === 'future'
            ? localisationMaster('text_32', 'online-schedule/future-slots')
            : localisationMaster('text_33', 'online-schedule/future-slots')
        }
      >
        <SearchBooks
          placeholder='Введите имя клиента'
          onSearch={handleSearch}
          onClear={handleClearSearch}
          translate={{ search: 'Поиск', filter: 'Фильтр' }}
          value={filter?.search || ''}
        />

        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...remainingFilters } = filter
              updateFilter(remainingFilters)
              setPage(1)
            }}
          />
        )}
      </ContentHeader>

      {isOpenFilter && (
        <AsideFilter
          getFilterParams={(newFilterParams) => {
            updateFilter(newFilterParams)
            setPage(1)
          }}
          config={filterConfig}
          filterValue={filter}
          onDropFilter={() => {
            updateFilter({})
            closeFilter()
            setPage(1)
          }}
          translate={translate}
        />
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <SortingBlock />
          <ClientsList />
          <ContentPagination
            page={page}
            allItems={totalClientsCount}
            per_page={clientsPerPage}
            changePage={(value) => setPage(value)}
            perPage={(value) => setClientsPerPage(value)}
          />
        </>
      )}
    </>
  )
}

export default observer(SlotListing)
